import {
  Component,
  Output,
  EventEmitter,
  HostListener,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  OnInit,
  OnDestroy,
  Injector,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { FormBuilderService } from '../../../../core/services/form-builder.service';
import { FormBuilderV2HistoryService } from '../../services/form-builder-v2-history.service';
import { FormBuilderV2StateService } from '../../services/form-builder-v2-state.service';
import { FbBuilderMode } from '../../models/form-builder-v2.models';

@Component({
  selector: 'irembogov-fb-toolbar',
  templateUrl: './fb-toolbar.component.html',
  styleUrls: ['./fb-toolbar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FbToolbarComponent implements OnInit, OnDestroy {
  @Output() toggleOutline = new EventEmitter<void>();

  canUndo = false;
  canRedo = false;
  builderMode: FbBuilderMode = 'builder';
  outlineVisible = false;

  private destroy$ = new Subject<void>();
  private formBuilderService: FormBuilderService;

  constructor(
    private injector: Injector,
    private historyService: FormBuilderV2HistoryService,
    private stateService: FormBuilderV2StateService,
    private cdr: ChangeDetectorRef
  ) {
    this.formBuilderService = this.injector.get(FormBuilderService);
  }

  ngOnInit(): void {
    this.historyService.canUndo$.pipe(takeUntil(this.destroy$)).subscribe(v => {
      this.canUndo = v;
      this.cdr.markForCheck();
    });

    this.historyService.canRedo$.pipe(takeUntil(this.destroy$)).subscribe(v => {
      this.canRedo = v;
      this.cdr.markForCheck();
    });

    this.stateService.builderMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe(mode => {
        this.builderMode = mode;
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onUndo(): void {
    const fields = this.historyService.undo();
    if (fields) {
      // Flag the restore so the central capture doesn't re-record it; emit with
      // forceReload so the canvas and code editor rebuild to the restored state.
      this.historyService.isRestoring = true;
      this.formBuilderService.formlyFieldsSubject$.next({
        fields,
        forceReload: true,
      });
    }
  }

  onRedo(): void {
    const fields = this.historyService.redo();
    if (fields) {
      this.historyService.isRestoring = true;
      this.formBuilderService.formlyFieldsSubject$.next({
        fields,
        forceReload: true,
      });
    }
  }

  onTogglePreview(): void {
    const newMode: FbBuilderMode =
      this.builderMode === 'builder' ? 'preview' : 'builder';
    this.stateService.setBuilderMode(newMode);
  }

  onToggleOutline(): void {
    this.outlineVisible = !this.outlineVisible;
    this.toggleOutline.emit();
    this.cdr.markForCheck();
  }

  @HostListener('document:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    const isCtrlOrMeta = event.ctrlKey || event.metaKey;
    if (isCtrlOrMeta && event.key === 'z' && !event.shiftKey) {
      event.preventDefault();
      this.onUndo();
    } else if (isCtrlOrMeta && event.key === 'z' && event.shiftKey) {
      event.preventDefault();
      this.onRedo();
    } else if (isCtrlOrMeta && event.key === 'y') {
      event.preventDefault();
      this.onRedo();
    }
  }
}
